import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Package2, Plus, Flag, Loader2, X, Trash2, Pencil, CalendarRange, List,
} from "lucide-react";
import {
  listSalesOrders, createSalesOrder, getSalesOrder, editSalesOrder, deleteSalesOrder,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import MasterCombobox from "@/components/MasterCombobox";
import { useAuth } from "@/contexts/AuthContext";
import OrdersKanban from "@/pages/OrdersKanban";

const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "confirmed", label: "Confirmed" },
  { key: "in_progress", label: "In progress" },
  { key: "delivered", label: "Delivered" },
  { key: "cancelled", label: "Cancelled" },
];

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  confirmed: "bg-blue-100 text-blue-800 border-blue-200",
  in_progress: "bg-amber-100 text-amber-800 border-amber-200",
  delivered: "bg-green-100 text-green-800 border-green-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

const KindPill = ({ kind }) => (
  <span
    className={`inline-flex items-center text-[9px] tracking-[0.15em] uppercase font-bold px-1.5 py-0.5 rounded-sm shrink-0 ${
      kind === "fg" ? "bg-emerald-100 text-emerald-800 border border-emerald-200" : "bg-blue-100 text-blue-800 border border-blue-200"
    }`}
  >
    {kind === "fg" ? "FG" : "SFG"}
  </span>
);

const OrderTile = ({ o, onOpen }) => {
  const balancePct = o.total_ordered_kg > 0 ? Math.round((o.total_delivered_kg / o.total_ordered_kg) * 100) : 0;
  const fgLines = (o.lines || []).filter((l) => (l.kind || "sfg") === "fg");
  const sfgLines = (o.lines || []).filter((l) => (l.kind || "sfg") === "sfg");
  return (
    <button
      data-testid={`order-row-${o.order_number}`}
      onClick={onOpen}
      className="text-left bg-white border border-slate-200 rounded-sm p-4 hover:border-slate-900 hover:shadow-md transition-all flex flex-col gap-2 min-h-[200px]"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono text-xs tracking-widest text-slate-500 font-semibold">{o.order_number}</p>
          <p className="font-heading text-lg font-bold text-slate-900 truncate mt-0.5">{o.customer_name}</p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {o.priority === "urgent" && (
            <span className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-bold px-1.5 py-0.5 rounded-sm bg-red-600 text-white">
              <Flag className="h-3 w-3" /> Urgent
            </span>
          )}
          <span className={`inline-flex items-center text-[10px] tracking-wider uppercase font-semibold px-1.5 py-0.5 rounded-sm border ${STATUS_STYLE[o.status] || ""}`}>{o.status.replace("_", " ")}</span>
        </div>
      </div>

      <div className="flex-1 space-y-1">
        {fgLines.length > 0 && (
          <div className="rounded-sm bg-emerald-50/60 border border-emerald-100 px-2 py-1">
            <p className="text-[9px] tracking-[0.2em] uppercase font-bold text-emerald-800 mb-0.5">FG</p>
            {fgLines.slice(0, 3).map((l) => (
              <div key={l.line_id || l.sku_name} className="flex items-center justify-between text-xs">
                <span className="font-mono font-semibold text-slate-900 truncate">{l.sku_name}</span>
                <span className="font-mono text-slate-600 shrink-0 ml-2">{l.ordered_kg} kg</span>
              </div>
            ))}
            {fgLines.length > 3 && <p className="text-[10px] text-emerald-700 italic">+{fgLines.length - 3} more…</p>}
          </div>
        )}
        {sfgLines.length > 0 && (
          <div className="rounded-sm bg-blue-50/60 border border-blue-100 px-2 py-1">
            <p className="text-[9px] tracking-[0.2em] uppercase font-bold text-blue-800 mb-0.5">SFG</p>
            {sfgLines.slice(0, 3).map((l) => (
              <div key={l.line_id || l.sku_name} className="flex items-center justify-between text-xs">
                <span className="font-mono font-semibold text-slate-900 truncate">{l.sku_name}</span>
                <span className="font-mono text-slate-600 shrink-0 ml-2">{l.ordered_kg} kg</span>
              </div>
            ))}
            {sfgLines.length > 3 && <p className="text-[10px] text-blue-700 italic">+{sfgLines.length - 3} more…</p>}
          </div>
        )}
      </div>

      <div className="border-t border-slate-100 pt-2 flex items-center justify-between text-[11px] text-slate-500">
        <span>Ordered {(o.order_date || "").slice(5) || "—"}</span>
        <span>Due {(o.target_delivery_date || "").slice(5) || "—"}</span>
      </div>

      <div className="bg-slate-100 rounded-full h-1.5 overflow-hidden">
        <div className={`h-full ${balancePct >= 100 ? "bg-green-500" : "bg-slate-900"}`} style={{ width: `${Math.min(100, balancePct)}%` }} />
      </div>
      <p className="text-[10px] text-slate-500 font-mono">{o.total_delivered_kg} / {o.total_ordered_kg} kg{o.total_balance_kg > 0 ? ` · ${o.total_balance_kg} balance` : ""}</p>
    </button>
  );
};

const LineFormRow = ({ line, onChange, onRemove, canRemove, idx }) => (
  <div className="grid grid-cols-12 gap-2 items-start bg-slate-50 border border-slate-200 rounded-sm p-2">
    <div className="col-span-2">
      <label className="text-[9px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Type *</label>
      <div className="mt-0.5 grid grid-cols-2 gap-0.5 border border-slate-300 rounded-sm overflow-hidden h-10">
        {["fg", "sfg"].map((k) => (
          <button
            key={k}
            type="button"
            data-testid={`line-kind-${k}-${idx}`}
            onClick={() => onChange({ kind: k })}
            className={`text-[10px] font-bold tracking-widest uppercase transition-colors ${
              line.kind === k
                ? (k === "fg" ? "bg-emerald-700 text-white" : "bg-blue-700 text-white")
                : "bg-white text-slate-500 hover:bg-slate-100"
            }`}
          >
            {k === "fg" ? "FG" : "SFG"}
          </button>
        ))}
      </div>
    </div>
    <div className="col-span-5">
      <label className="text-[9px] tracking-[0.2em] uppercase text-slate-500 font-semibold">SKU *</label>
      <MasterCombobox
        masterType={line.kind === "fg" ? "fg_sku" : "sfg_sku"}
        value={line.sku_name}
        onChange={(v) => onChange({ sku_name: v })}
        placeholder={line.kind === "fg" ? "EI-114" : "50C470"}
        testId={`line-sku-${idx}`}
        allowFreeText
      />
    </div>
    <div className="col-span-3">
      <label className="text-[9px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Ordered kg *</label>
      <input
        data-testid={`line-kg-${idx}`}
        type="number"
        step="1"
        value={line.ordered_kg ?? ""}
        onChange={(e) => onChange({ ordered_kg: parseFloat(e.target.value) || 0 })}
        className="mt-0.5 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono"
      />
    </div>
    <div className="col-span-2 flex justify-end pt-4">
      {canRemove && (
        <button type="button" data-testid={`line-remove-${idx}`} onClick={onRemove} className="text-red-600 p-2 hover:bg-red-50 rounded-sm"><Trash2 className="h-4 w-4" /></button>
      )}
    </div>
  </div>
);

let _lineUidSeq = 0;
const newLineDraft = (patch = {}) => ({ _uid: `line-${++_lineUidSeq}`, kind: "fg", sku_name: "", ordered_kg: "", ...patch });

const OrderForm = ({ existing = null, onSubmit, onCancel, submitting }) => {
  const [customer, setCustomer] = useState(existing?.customer_name || "");
  const [customerId, setCustomerId] = useState(existing?.customer_id || "");
  const [po, setPo] = useState(existing?.po_number || "");
  const [priority, setPriority] = useState(existing?.priority || "normal");
  const [target, setTarget] = useState(existing?.target_delivery_date || "");
  const [notes, setNotes] = useState(existing?.notes || "");
  const [lines, setLines] = useState(existing?.lines?.length
    ? existing.lines.map((l) => newLineDraft({ kind: l.kind || "sfg", sku_name: l.sku_name, ordered_kg: l.ordered_kg }))
    : [newLineDraft()]);

  const updateLine = (i, patch) => setLines((arr) => arr.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const addLine = () => setLines((arr) => [...arr, newLineDraft()]);
  const removeLine = (i) => setLines((arr) => arr.filter((_, idx) => idx !== i));

  const submit = () => {
    if (!customer.trim()) { toast.error("Customer is required"); return; }
    const clean = lines.filter((l) => l.sku_name?.trim() && parseFloat(l.ordered_kg) > 0);
    if (clean.length === 0) { toast.error("Add at least one line with a SKU and quantity"); return; }
    onSubmit({
      customer_name: customer.trim(),
      customer_id: customerId || "",     // preserves live link when non-empty
      po_number: po.trim(),
      priority,
      target_delivery_date: target,
      notes,
      lines: clean.map((l) => ({
        kind: l.kind === "fg" ? "fg" : "sfg",
        sku_name: l.sku_name.trim(),
        ordered_kg: parseFloat(l.ordered_kg),
      })),
    });
  };

  return (
    <div data-testid="order-form" className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Customer *</label>
          <div className="mt-1">
            <MasterCombobox
              masterType="customer"
              value={customer}
              onChange={setCustomer}
              onPickMaster={(m) => setCustomerId(m?.id || "")}
              placeholder="LPTL"
              testId="order-customer"
              allowFreeText
            />
          </div>
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">PO number</label>
          <input data-testid="order-po" value={po} onChange={(e) => setPo(e.target.value)} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono uppercase" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Priority</label>
          <select data-testid="order-priority" value={priority} onChange={(e) => setPriority(e.target.value)} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm">
            <option value="normal">Normal</option>
            <option value="urgent">🚩 Urgent</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Target delivery date</label>
          <input data-testid="order-target-date" type="date" value={target} onChange={(e) => setTarget(e.target.value)} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm font-mono" />
        </div>
      </div>

      <div>
        <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold mb-2">Line items</p>
        <div className="space-y-2">
          {lines.map((line, i) => (
            <LineFormRow key={line._uid} idx={i} line={line} onChange={(p) => updateLine(i, p)} onRemove={() => removeLine(i)} canRemove={lines.length > 1} />
          ))}
        </div>
        <button type="button" data-testid="add-line-btn" onClick={addLine} className="mt-2 w-full h-11 border-2 border-dashed border-slate-300 rounded-sm text-slate-700 hover:bg-slate-50 hover:border-slate-900 flex items-center justify-center gap-2 text-sm font-semibold">
          <Plus className="h-4 w-4" /> Add another line
        </button>
      </div>

      <div>
        <label className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Notes</label>
        <input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full h-10 px-2 border border-slate-300 rounded-sm text-sm" />
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
        <Button variant="outline" onClick={onCancel} disabled={submitting}>Cancel</Button>
        <Button data-testid="order-submit" onClick={submit} disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white">
          {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : (existing ? "Save changes" : "Create order")}
        </Button>
      </div>
    </div>
  );
};

const OrderDetail = ({ order, onClose, onEdit }) => {
  const { hasModule } = useAuth();
  const qc = useQueryClient();
  const canEdit = hasModule("sales_orders.edit");
  const canDelete = hasModule("sales_orders.delete");

  const deleteMut = useMutation({
    mutationFn: () => deleteSalesOrder(order.id),
    onSuccess: () => { toast.success("Order deleted"); onClose(); qc.invalidateQueries({ queryKey: ["orders"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  return (
    <div data-testid="order-detail" className="bg-white rounded-sm border border-slate-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-heading text-2xl font-bold text-slate-900 font-mono tracking-tight">{order.order_number}</p>
            <span className={`inline-flex items-center text-[10px] tracking-wider uppercase font-semibold px-1.5 py-0.5 rounded-sm border ${STATUS_STYLE[order.status] || ""}`}>{order.status.replace("_", " ")}</span>
            {order.priority === "urgent" && (
              <span className="inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-bold px-1.5 py-0.5 rounded-sm bg-red-600 text-white">
                <Flag className="h-3 w-3" /> Urgent
              </span>
            )}
          </div>
          <p className="text-sm text-slate-700 mt-1"><b>{order.customer_name}</b>{order.po_number && ` · PO ${order.po_number}`}{order.target_delivery_date && ` · Due ${order.target_delivery_date}`}</p>
          <p className="text-xs text-slate-500 mt-0.5">Created {(order.created_at || "").slice(0, 16).replace("T", " ")} by {order.created_by}</p>
        </div>
        <button data-testid="close-order-detail" onClick={onClose} className="text-slate-400 hover:text-slate-900 p-1"><X className="h-5 w-5" /></button>
      </div>

      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex flex-wrap gap-2">
        {canEdit && order.status !== "delivered" && order.status !== "cancelled" && (
          <Button size="sm" variant="outline" data-testid="edit-order-btn" onClick={onEdit}><Pencil className="h-4 w-4 mr-1.5" /> Edit</Button>
        )}
        {canDelete && order.total_delivered_kg === 0 && (
          <Button size="sm" variant="outline" className="text-red-700 border-red-200 hover:bg-red-50" data-testid="delete-order-btn"
            onClick={() => { if (window.confirm(`Delete order ${order.order_number}?`)) deleteMut.mutate(); }}
          ><Trash2 className="h-4 w-4 mr-1.5" /> Delete</Button>
        )}
        <div className="ml-auto flex items-center gap-4 text-xs">
          <div><span className="text-slate-500">Ordered</span> <b className="font-mono text-slate-900">{order.total_ordered_kg} kg</b></div>
          <div><span className="text-slate-500">Delivered</span> <b className="font-mono text-green-700">{order.total_delivered_kg} kg</b></div>
          <div><span className="text-slate-500">Balance</span> <b className={`font-mono ${order.total_balance_kg > 0 ? "text-amber-700" : "text-slate-500"}`}>{order.total_balance_kg} kg</b></div>
        </div>
      </div>

      <div className="px-5 py-4 space-y-2" data-testid="order-lines">
        {/* Group lines: FG first, then SFG */}
        {["fg", "sfg"].flatMap((groupKind) => {
          const groupLines = (order.lines || [])
            .map((line, origIdx) => ({ line, origIdx }))
            .filter(({ line }) => (line.kind || "sfg") === groupKind);
          if (groupLines.length === 0) return [];
          return [
            <p key={`hdr-${groupKind}`} className={`text-[10px] tracking-[0.2em] uppercase font-bold ${groupKind === "fg" ? "text-emerald-800" : "text-blue-800"} pt-2`}>
              {groupKind === "fg" ? "FG lines" : "SFG lines"}
            </p>,
            ...groupLines.map(({ line, origIdx }) => {
              const balance = Math.max(0, (line.ordered_kg || 0) - (line.delivered_kg || 0));
              const closed = !!line.short_closed_at || !!line.delivered_at;
              const kind = line.kind || "sfg";
              const wStatus = line.weight_status;   // "exact" | "over" | "under" | ""
              const wDiff = line.weight_diff_kg;
              return (
                <div key={line.line_id} data-testid={`order-line-${origIdx}`} className={`border rounded-sm p-3 ${closed ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white"}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <KindPill kind={kind} />
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-sm font-bold text-slate-900">
                        {line.sku_name}
                        {line.grade && <span className="text-slate-500 font-normal"> · {line.grade}</span>}
                        {line.width_mm ? <span className="text-slate-500 font-normal"> · {line.width_mm}mm</span> : null}
                      </p>
                      <p className="text-xs text-slate-600 mt-1 font-mono">
                        <span className="text-green-700 font-bold">{line.delivered_kg} kg</span> / {line.ordered_kg} kg
                        {balance > 0 && !closed && <span className="text-amber-700"> · {balance} kg balance</span>}
                        {closed && line.delivered_at && <span className="text-slate-500"> · CLOSED {new Date(line.delivered_at).toLocaleDateString()}</span>}
                        {closed && line.short_closed_at && !line.delivered_at && <span className="text-slate-500"> · SHORT-CLOSED</span>}
                      </p>
                    </div>
                    {wStatus && (
                      <span
                        data-testid={`line-weight-status-${origIdx}`}
                        className={`inline-flex items-center gap-1 text-[10px] tracking-wider uppercase font-bold px-1.5 py-0.5 rounded-sm ${
                          wStatus === "over"
                            ? "bg-red-100 text-red-800 border border-red-200"
                            : wStatus === "under"
                              ? "bg-amber-100 text-amber-800 border border-amber-200"
                              : "bg-green-100 text-green-800 border border-green-200"
                        }`}
                      >
                        {wStatus === "exact" ? "Exact weight" : `${wStatus === "over" ? "Over" : "Under"} weight${typeof wDiff === "number" ? ` (${wDiff > 0 ? "+" : ""}${wDiff.toFixed(1)} kg)` : ""}`}
                      </span>
                    )}
                  </div>
                </div>
              );
            }),
          ];
        })}
      </div>
    </div>
  );
};

const Orders = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("list"); // list | kanban
  const [filter, setFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const params = useMemo(() => (filter === "all" ? {} : { status: filter }), [filter]);
  const { data: orders = [], isLoading } = useQuery({ queryKey: ["orders", params], queryFn: () => listSalesOrders(params) });
  const { data: openOrder } = useQuery({ queryKey: ["order", openId], queryFn: () => getSalesOrder(openId), enabled: !!openId, refetchInterval: openId ? 4000 : false });

  const createMut = useMutation({
    mutationFn: createSalesOrder,
    onSuccess: (d) => { toast.success(`${d.order_number} created`); setCreateOpen(false); setOpenId(d.id); qc.invalidateQueries({ queryKey: ["orders"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const editMut = useMutation({
    mutationFn: (payload) => editSalesOrder(openId, payload),
    onSuccess: () => { toast.success("Order updated"); setEditMode(false); qc.invalidateQueries({ queryKey: ["orders"] }); qc.invalidateQueries({ queryKey: ["order", openId] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  return (
    <div data-testid="orders-page" className="max-w-6xl mx-auto space-y-5">
      <div className="border-b border-slate-200 pb-4 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Package2 className="h-7 w-7 text-slate-900" />
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-semibold">Sales Orders</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Customer orders</h1>
          </div>
        </div>
        <Button data-testid="new-order-btn" onClick={() => setCreateOpen(true)} className="bg-slate-900 hover:bg-slate-800 text-white">
          <Plus className="h-4 w-4 mr-1.5" /> New order
        </Button>
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200">
        <button
          data-testid="tab-list"
          onClick={() => setTab("list")}
          className={`h-10 px-4 text-xs font-semibold uppercase tracking-wider border-b-2 flex items-center gap-2 -mb-px ${
            tab === "list" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
          }`}
        >
          <List className="h-4 w-4" /> Orders
        </button>
        <button
          data-testid="tab-kanban"
          onClick={() => setTab("kanban")}
          className={`h-10 px-4 text-xs font-semibold uppercase tracking-wider border-b-2 flex items-center gap-2 -mb-px ${
            tab === "kanban" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
          }`}
        >
          <CalendarRange className="h-4 w-4" /> Weekly plan
        </button>
      </div>

      {tab === "kanban" && <OrdersKanban />}

      {tab === "list" && <>
      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((f) => (
          <button key={f.key} data-testid={`orders-filter-${f.key}`} onClick={() => setFilter(f.key)}
            className={`h-9 px-3 rounded-sm text-xs font-semibold uppercase tracking-wider border transition-colors ${filter === f.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {isLoading && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}

      {!isLoading && orders.length === 0 && (
        <div data-testid="orders-empty" className="bg-white border border-dashed border-slate-300 rounded-sm py-14 text-center text-sm text-slate-500">
          No orders in this view.
          <Button onClick={() => setCreateOpen(true)} className="ml-3 bg-slate-900 hover:bg-slate-800 text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Create first order
          </Button>
        </div>
      )}

      {!isLoading && orders.length > 0 && (
        <div data-testid="orders-list" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {orders.map((o) => <OrderTile key={o.id} o={o} onOpen={() => { setOpenId(o.id); setEditMode(false); }} />)}
        </div>
      )}
      </>}

      {createOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-40 flex items-start justify-center p-4 overflow-auto">
          <div className="bg-white rounded-sm w-full max-w-4xl mt-8 mb-8 p-5" data-testid="create-order-modal">
            <div className="flex items-center justify-between mb-4 border-b border-slate-200 pb-3">
              <h2 className="font-heading text-xl font-bold text-slate-900">New sales order</h2>
              <button onClick={() => setCreateOpen(false)} className="text-slate-400 hover:text-slate-900 text-xl leading-none">×</button>
            </div>
            <OrderForm onSubmit={(p) => createMut.mutate(p)} onCancel={() => setCreateOpen(false)} submitting={createMut.isPending} />
          </div>
        </div>
      )}

      {openId && (
        <div className="fixed inset-0 bg-slate-900/60 z-40 flex items-start justify-center p-4 overflow-auto">
          <div className="w-full max-w-4xl mt-8 mb-8 relative">
            {!openOrder && <div className="bg-white rounded-sm p-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" /></div>}
            {openOrder && !editMode && (
              <OrderDetail order={openOrder} onClose={() => setOpenId(null)} onEdit={() => setEditMode(true)} />
            )}
            {openOrder && editMode && (
              <div className="bg-white rounded-sm p-5">
                <div className="flex items-center justify-between mb-4 border-b border-slate-200 pb-3">
                  <h2 className="font-heading text-xl font-bold text-slate-900">Edit {openOrder.order_number}</h2>
                  <button onClick={() => setEditMode(false)} className="text-slate-400 hover:text-slate-900 text-xl leading-none">×</button>
                </div>
                <OrderForm existing={openOrder} onSubmit={(p) => editMut.mutate(p)} onCancel={() => setEditMode(false)} submitting={editMut.isPending} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
